// src/components/AlarmsTable.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  LocalHospital as EmergencyIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { alarmsApi } from '../services/apiClient';

// Status color mapping
const getStatusColor = (stateCode) => {
  switch (stateCode) {
    case 'countdown':
      return 'warning';
    case 'sent_to_emergency':
      return 'error';
    case 'acknowledged':
      return 'info';
    case 'dispatched':
      return 'primary';
    case 'finished':
      return 'success';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

// Status icon mapping
const getStatusIcon = (stateCode) => {
  switch (stateCode) {
    case 'countdown':
      return <ScheduleIcon fontSize="small" />;
    case 'sent_to_emergency':
      return <ErrorIcon fontSize="small" />;
    case 'acknowledged':
      return <InfoIcon fontSize="small" />;
    case 'dispatched':
      return <EmergencyIcon fontSize="small" />;
    case 'finished':
      return <CheckCircleIcon fontSize="small" />;
    case 'cancelled':
      return <WarningIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
};

const AlarmsTable = ({ locationId, onViewAlarm }) => {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Detail dialog state
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [alarmDetail, setAlarmDetail] = useState(null);

  useEffect(() => {
    if (locationId) {
      loadAlarms();
    }
  }, [locationId, page, rowsPerPage]);

  const loadAlarms = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await alarmsApi.getAlarmHistory({
        locationId,
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });

      const data = response.data?.data || {};
      setAlarms(data.alarms || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load alarms:', err);
      setError('Failed to load alarm history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAlarm = async (alarm) => {
    if (onViewAlarm) {
      onViewAlarm(alarm);
      return;
    }

    setSelectedAlarm(alarm);
    setDetailDialogOpen(true);
    setDetailLoading(true);

    try {
      const response = await alarmsApi.getAlarmById(alarm.id);
      setAlarmDetail(response.data?.data || null);
    } catch (err) {
      console.error('Failed to load alarm details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setAlarmDetail(null);
    setSelectedAlarm(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && alarms.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={loadAlarms} sx={{ mt: 1 }}>Retry</Button>
      </Box>
    );
  }

  if (alarms.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <EmergencyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography color="text.secondary">
          No alarms found for this location
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Person</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Finished</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alarms.map((alarm) => (
              <TableRow
                key={alarm.id}
                hover
                sx={{
                  backgroundColor: !alarm.isTerminal && alarm.stateCode === 'sent_to_emergency'
                    ? 'error.light'
                    : 'inherit'
                }}
              >
                <TableCell>
                  <Chip
                    icon={getStatusIcon(alarm.stateCode)}
                    label={alarm.state}
                    color={getStatusColor(alarm.stateCode)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{alarm.type || 'Unknown'}</TableCell>
                <TableCell>
                  {alarm.person ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon fontSize="small" color="action" />
                      {alarm.person}
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell>{alarm.device || '-'}</TableCell>
                <TableCell>
                  <Tooltip title={format(new Date(alarm.firstPressAt), 'PPpp')}>
                    <span>{formatDistanceToNow(new Date(alarm.firstPressAt), { addSuffix: true })}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {alarm.finishedAt ? (
                    <Tooltip title={format(new Date(alarm.finishedAt), 'PPpp')}>
                      <span>{formatDistanceToNow(new Date(alarm.finishedAt), { addSuffix: true })}</span>
                    </Tooltip>
                  ) : '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewAlarm(alarm)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Alarm Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmergencyIcon color="error" />
            Alarm Details
            {alarmDetail?.alarmStateModel && (
              <Chip
                label={alarmDetail.alarmStateModel.friendlyName}
                color={getStatusColor(alarmDetail.alarmStateModel.code)}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : alarmDetail ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.alarmTypeModel?.friendlyName || 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Alarm ID</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.alarm_UUID}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.locationModel?.friendlyName || 'Unknown'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Person</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.personModel?.friendlyName || 'Unknown'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Device</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.deviceModel?.friendlyName || 'Unknown'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Notifications Sent</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.notificationCount || 0}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" gutterBottom>Timeline</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">First Press</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.firstPressAt ? format(new Date(alarmDetail.firstPressAt), 'PPpp') : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Sent to Emergency</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.sentToEmergencyAt ? format(new Date(alarmDetail.sentToEmergencyAt), 'PPpp') : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Acknowledged</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.acknowledgedAt ? format(new Date(alarmDetail.acknowledgedAt), 'PPpp') : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Finished</Typography>
                <Typography variant="body1" gutterBottom>
                  {alarmDetail.finishedAt ? format(new Date(alarmDetail.finishedAt), 'PPpp') : '-'}
                </Typography>
              </Grid>

              {alarmDetail.locationReasoning && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">Location Reasoning</Typography>
                  <Typography variant="body1" gutterBottom>
                    {alarmDetail.locationReasoning}
                  </Typography>
                </Grid>
              )}

              {alarmDetail.events && alarmDetail.events.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" gutterBottom>Events ({alarmDetail.events.length})</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Time</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {alarmDetail.events.map((event, index) => (
                          <TableRow key={event.id || index}>
                            <TableCell>
                              {event.eventTimestamp ? format(new Date(event.eventTimestamp), 'HH:mm:ss') : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip label={event.eventType} size="small" />
                            </TableCell>
                            <TableCell>
                              {event.locationReasoning || event.antennaName || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography color="text.secondary">No alarm details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlarmsTable;
